/* eslint-disable no-unused-vars */
import { NotificationTarget } from "../Repositories/NotificationRepository";
import { getActiveBrandConfig, getActivePrestationBrandConfig, getPathClientBrandConfig } from "./environment.config";

/**
 * State de navigation d'une notification : rubrique des paramètres (step, lue par
 * SidebarNavLayout) ou, sur la page documents, catégorie à présélectionner
 * (documentsCategory, lue par DocumentSpace : clé de ses onglets, ex. "transport").
 */
export interface NotificationLocationState {
  step?: number;
  documentsCategory?: string;
}

/** Destination web d'une notification : chemin absolu et éventuel state de navigation. */
export interface NotificationDestination {
  path: string;
  state?: NotificationLocationState;
}

type NotificationRouteResolver = (params: Record<string, unknown>, isClient: boolean) => NotificationDestination | null;

// Onglet des abonnements de transport dans DocumentSpace (clé de CATEGORY_LABELS)
const TRANSPORT_DOCUMENTS_CATEGORY = "transport";

const dashboardPath = () => `/${getActiveBrandConfig()}/tableau-de-bord`;

// Identifiant ou numéro d'étape reçu du serveur : entier strictement positif, sinon ignoré
const toPositiveInt = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

// Documents : entrée « Mes documents » du menu, pour le prof comme pour le client
const documentsDestination = (): NotificationDestination => ({ path: `${dashboardPath()}/documents` });

/**
 * Route logique (target.route renvoyé par l'API) vers une URL du tableau de bord unifié.
 * Les chemins sont construits à l'appel pour suivre la marque active. Une route inconnue
 * est ignorée : un backend en avance sur le front ne casse rien.
 */
export const NOTIFICATION_ROUTES: Record<string, NotificationRouteResolver> = {
  // fiche d'un client : réservée au prof
  client_detail: (params, isClient) => {
    const clientId = toPositiveInt(params.clientId);
    if (isClient || clientId === null) return null;
    return { path: `${dashboardPath()}/${getPathClientBrandConfig()}/${clientId}` };
  },
  // liste des clients : réservée au prof
  client_list: (_params, isClient) => (
    isClient ? null : { path: `${dashboardPath()}/${getPathClientBrandConfig()}` }
  ),
  courses: () => ({ path: `${dashboardPath()}/${getActivePrestationBrandConfig()}` }),
  documents: () => documentsDestination(),
  // page complète du centre de notifications, commune au prof et au client
  notifications: () => ({ path: `${dashboardPath()}/notifications` }),
  // paramètres, éventuellement sur une rubrique précise (numérotation propre à chaque rôle)
  settings: (params) => {
    const step = toPositiveInt(params.step);
    return step === null
      ? { path: `${dashboardPath()}/parametre` }
      : { path: `${dashboardPath()}/parametre`, state: { step } };
  },
  // abonnement de transport : page documents du prof, onglet des abonnements
  travel_subscription: (_params, isClient) => (
    isClient ? documentsDestination() : { ...documentsDestination(), state: { documentsCategory: TRANSPORT_DOCUMENTS_CATEGORY } }
  ),
};

/** Traduit la cible d'une notification en destination, ou null s'il n'y a nulle part où aller. */
export const resolveNotificationRoute = (
  target: NotificationTarget | null | undefined,
  isClient: boolean,
): NotificationDestination | null => {
  if (!target || typeof target.route !== "string") return null;
  if (!Object.prototype.hasOwnProperty.call(NOTIFICATION_ROUTES, target.route)) return null;
  const params = target.params && typeof target.params === "object" ? target.params : {};
  return NOTIFICATION_ROUTES[target.route](params, isClient);
};
